import type { ProspectResponse } from '@common/types/prospects';
import { getMatterTypeLabel } from '@common/util';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    type SxProps,
    type Theme,
    Typography,
    useTheme,
} from '@mui/material';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import EditableFullname from '@/components/ReadOnlyComponent/EditableFullname';
import EditableViewComponent from '@/components/ReadOnlyComponent/EditableViewComponent';
import { ordinalNumbers } from '@/constants';
import { patchProspectById } from '@/services/ProspectService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import ContactEmailBounceAlert from './ContactEmailBounceAlert';
import ContactEmailBounceChip from './ContactEmailBounceChip';

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
    onProspectChange: (prospect: ProspectResponse) => void;
    handleValueChange: (name: string, value: any) => void;
    readOnly: boolean;
}

export default function SaleProspectInformation({
    prospectId,
    prospect,
    onProspectChange,
    handleValueChange,
}: Props) {
    const theme = useTheme();
    const [isFullnameModalOpen, setIsFullnameModalOpen] = useState(false);
    const [fullnameAnchorEl, setFullnameAnchorEl] = useState<HTMLButtonElement | null>(null);
    const fullnameModalId = isFullnameModalOpen ? 'fullname-popover' : undefined;

    //styles
    const containerStyle: SxProps<Theme> = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.neutral.borderColor}`,
        paddingBottom: '10px',
        width: '100%',
        gap: '20px',
        marginTop: '',
    };

    // Methods
    const nameClickHandler = (event: MouseEvent<HTMLButtonElement> | null = null) => {
        setIsFullnameModalOpen((prev) => {
            if (!prev && event) {
                setFullnameAnchorEl(event.currentTarget);
            } else {
                setFullnameAnchorEl(null);
            }
            return !prev;
        });
    };

    const handleChange = async (value: string) => {
        if ((!value && prospect.customFileNumber === '') || value === prospect.customFileNumber)
            return;

        if (value && value?.length > 400) {
            errorSnackbar('Custom file number must be less than 400 characters.');
            return;
        }

        try {
            const response = await patchProspectById(prospectId as string, [
                {
                    op: 'replace',
                    path: '/customFileNumber',
                    value: value,
                },
            ]);
            onProspectChange(response);
            successSnackbar('Custom file number updated successfully!');
        } catch (error) {
            console.error('API error:', error);
        }
    };

    const accordionStyles: SxProps<Theme> = {
        width: '100%',
        border: `1px solid ${theme.palette.neutral.N_300}`,
        borderRadius: '4px !important',
        boxShadow: 'none !important',
        '&.MuiAccordion-root.Mui-expanded': {
            boxShadow: '0px 4px 10px 0px #2F303233 !important',
            border: '0px !important',
        },

        '&::before': {
            content: 'none',
        },
    };

    let displayHeader = '';

    if (prospect.matterType === 'PrivateLender') {
        displayHeader = 'Lender(s) info';
    } else if (prospect.matterType === 'Purchase') {
        displayHeader = 'Purchaser(s) info';
    } else if (prospect.matterType === 'Sale') {
        displayHeader = 'Seller(s) info';
    } else if (prospect.matterType === 'Refinance') {
        displayHeader = 'Borrower(s) info';
    } else {
        displayHeader = 'Contact info';
    }
    return (
        <>
            <Typography variant="h3">Prospect information</Typography>
            <EditableViewComponent
                label="Custom file number"
                value={prospect.customFileNumber}
                isEditable={true}
                onChange={handleChange}
                maxLength={400}
                errorMessage="Custom file number must be less than 400 characters."
            />
            <EditableViewComponent label="Prospect name" value={prospect.prospectName} />
            <EditableViewComponent
                label="Matter type"
                value={getMatterTypeLabel(prospect.matterType)}
            />
            {prospect.quoteTemplate?.quoteTemplateName && (
                <EditableViewComponent
                    label="Quote template"
                    value={prospect.quoteTemplate.quoteTemplateName}
                    link={`/quote/${prospect.quoteTemplateId}`}
                />
            )}
            {prospect?.multipleContacts.length > 1 ? (
                prospect?.multipleContacts.map((el, i) => (
                    <Accordion
                        key={`accordion-${i}`}
                        // defaultExpanded={true}
                        sx={accordionStyles}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h3">
                                    {`${ordinalNumbers[i]} ${displayHeader}`}
                                </Typography>
                                <ContactEmailBounceChip contact={el} />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ContactEmailBounceAlert contact={el} sx={{ marginBottom: '10px' }} />
                            <Box sx={containerStyle}>
                                <Typography variant="body2" sx={{ width: '50%' }}>
                                    Client
                                </Typography>
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {el.contactName || 'NA'}
                                </Box>
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <EditableFullname
                                    id={fullnameModalId}
                                    open={isFullnameModalOpen}
                                    anchorEl={fullnameAnchorEl}
                                    onClose={nameClickHandler}
                                    firstName={el.firstName}
                                    middleName={el.middleName ?? ''}
                                    lastName={el.lastName}
                                    handleValueChange={handleValueChange}
                                    onChange={() => {}}
                                />
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <EditableViewComponent label="Email" value={el.contactEmail} />
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <EditableViewComponent label="Cell number" value={el.phoneNumber} />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))
            ) : (
                <>
                    <Box sx={containerStyle}>
                        <Typography variant="body2" sx={{ width: '50%' }}>
                            Client
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {prospect?.contact?.contactName || 'NA'}
                        </Box>
                    </Box>
                    <EditableFullname
                        id={fullnameModalId}
                        open={isFullnameModalOpen}
                        anchorEl={fullnameAnchorEl}
                        onClose={nameClickHandler}
                        firstName={prospect?.contact?.firstName}
                        middleName={prospect?.contact?.middleName ?? ''}
                        lastName={prospect?.contact?.lastName}
                        handleValueChange={handleValueChange}
                        onChange={() => {}}
                    />
                    <EditableViewComponent label="Email" value={prospect?.contact?.contactEmail} />
                    <ContactEmailBounceAlert contact={prospect?.contact} />
                    <EditableViewComponent
                        label="Cell number"
                        value={prospect?.contact?.phoneNumber}
                    />
                </>
            )}
        </>
    );
}
